import { Injectable } from '@angular/core';
import { environment } from '../../environments/environment';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { AuthService } from './auth.service';
import { Observable, Subject, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class DashboardService {
  private apiUrl = environment.api.url;

  token: string;

  constructor(private http: HttpClient, private authService: AuthService) {}

  private dashboardSource = new Subject<string>();

  dashboard$ = this.dashboardSource.asObservable();

  getDashboardInfo(): Observable<{ data: any; message: string }> {
    const url = `${this.apiUrl}/dashboard-info`;

    // Fetch the token from AuthService
    this.authService.token().subscribe((token) => {
      this.token = token;
    });

    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
        Authorization: `Bearer ${this.token}`, // Ensure the token is properly fetched
      }),
    };

    return this.http.get<any>(url, httpOptions).pipe(
      map((response) => {
        // console.log('[dashboard.service.ts] response', response);

        if (!response.data) {
          throw new Error('Dashboard failed: ' + response.message);
        }

        return {
          data: response.data,
          message: response.message,
        };
      }),
      catchError((error) => {
        return throwError(error);
      })
    );
  }

  getServicesList(): Observable<{ data: any; message: string }> {
    const url = `${this.apiUrl}/services/list`;

    // Fetch the token from AuthService
    this.authService.token().subscribe((token) => {
      this.token = token;
    });

    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
        Authorization: `Bearer ${this.token}`, // Ensure the token is properly fetched
      }),
    };

    return this.http.get<any>(url, httpOptions).pipe(
      map((response) => {
        // console.log('[dashboard.service.ts] response', response);

        if (!response.data) {
          throw new Error('Dashboard failed: ' + response.message);
        }

        return {
          data: response.data,
          message: response.message,
        };
      }),
      catchError((error) => {
        return throwError(error);
      })
    );
  }

  storeService(
    serviceId: number,
    message: string
  ): Observable<{ data: any; message: string }> {
    const url = `${this.apiUrl}/services/request`; // Replace with the correct endpoint

    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
        Authorization: `Bearer ${this.token}`, // Ensure the token is properly fetched
      }),
    };

    const data = {
      service_id: serviceId,
      message: message,
    };

    return this.http.post<any>(url, data, httpOptions).pipe(
      map((response) => {
        // console.log('[dashboard.service.ts] response', response);

        if (!response.data) {
          throw new Error(
            'Store service generation failed: ' + response.message
          );
        }

        return {
          data: response.data,
          message: response.message,
        };
      }),
      catchError((error) => {
        return throwError(error);
      })
    );
  }

  uploadCSV(csvFile: File, batchType: string = 'email'): Observable<{ data: any }> {
    console.log('[dashboard.service.ts] uploadCSV with batchType:', batchType);

    const url = `${this.apiUrl}/events/upload-csv`; // Replace with the correct endpoint

    const httpOptions = {
      headers: new HttpHeaders({
        Authorization: `Bearer ${this.token}`,
      }),
    };

    const formData = new FormData();
    formData.append('batch_type', batchType); // Use the provided batch type
    formData.append('file', csvFile); // Attach the file

    return this.http.post<any>(url, formData, httpOptions).pipe(
      map((response) => {
        if (!response.processing_result) {
          throw new Error('CSV Upload failed: ' + response.message);
        }

        return {
          data: response,
        };
      }),
      catchError((error) => {
        return throwError(error);
      })
    );
  }

  uploadEmails(emails: string[]): Observable<{ data: any }> {
    console.log('[dashboard.service.ts] uploadEmails with count:', emails.length);

    const url = `${this.apiUrl}/events/upload-emails`;

    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
        Authorization: `Bearer ${this.token}`,
      }),
    };

    // Create request payload - ensure the API expects this structure
    const payload = {
      emails: emails
    };

    console.log('[dashboard.service.ts] Sending email payload:', payload);

    return this.http.post<any>(url, payload, httpOptions).pipe(
      map((response) => {
        // Log the response to help with debugging
        console.log('[dashboard.service.ts] Email upload response:', response);

        // More lenient success condition
        if (response && response.success === false) {
          throw new Error('Email Upload failed: ' + (response.message || 'Unknown error'));
        }

        return {
          data: response,
          success: true
        };
      }),
      catchError((error) => {
        console.error('[dashboard.service.ts] Email upload error:', error);
        return throwError(error);
      })
    );
  }
}
